import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { startInvoiceProcessFromFiles } from './startInvoiceProcessFromFiles';
import {
  type StartProcessFromFilesRequestParameters,
  type StartProcessFromFilesResponse,
} from './types';

/**
 * A custom hook to start a new invoice process using the given files and parameters.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/start_process_from_files_invoice_process_start_from_files_post
 */
export function useMutationStartProcessFromFiles(
  options?: UseMutationOptions<
    StartProcessFromFilesResponse,
    Error,
    StartProcessFromFilesRequestParameters
  >,
) {
  const queryClient = useQueryClient();

  return useMutation<
    StartProcessFromFilesResponse,
    Error,
    StartProcessFromFilesRequestParameters
  >({
    mutationFn: startInvoiceProcessFromFiles,
    onSettled() {
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceReprocessing.getAll({}),
      });
    },
    onSuccess() {
      ToastService.success(
        i18n.t(($) => $.startProcessFromFiles.success, {
          ns: 'data',
        }),
      );
    },
    ...options,
  });
}
